import { useCallback, useEffect, useRef } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Icon } from './Icon';
import { cn } from '../../lib/cn';
import { haptics } from '../../lib/haptics';

interface NumberStepperProps {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  large?: boolean;
  suffix?: string;
  fractionDigits?: number;
  ariaLabel?: string;
  /**
   * Optional quick-jump increments rendered as smaller labeled buttons
   * flanking the main +/-. Each entry produces a -N button on the left
   * (outermost first) and a +N button on the right. For a baseline of
   * 300mg, [10, 100] saves ~298 taps versus single-step increment.
   */
  quickSteps?: number[];
  /**
   * Press-and-hold the central +/- to jump by `step * multiplier` in one
   * gesture. Tapping still fires the single-step increment. Lets the dose
   * sheet keep a clean 5-button layout while exposing ±10 / ±100 jumps.
   */
  holdJump?: { durationMs?: number; multiplier?: number };
  /**
   * Fire haptics.warn() (in addition to the per-tap confirm tick) when
   * the new value crosses one of these boundary values — gives the
   * stepper a wheel-with-detents feel at meaningful thresholds (5, 10,
   * 100 mg). Caller controls whether haptics fire at all by passing this
   * prop or omitting it.
   */
  hapticBoundaries?: number[];
}

export function NumberStepper({
  value,
  onChange,
  step = 1,
  min = 0,
  max = Number.POSITIVE_INFINITY,
  large,
  suffix,
  fractionDigits = 1,
  ariaLabel,
  quickSteps,
  holdJump,
  hapticBoundaries,
}: NumberStepperProps) {
  const display = `${round(value, fractionDigits)}${suffix ? ` ${suffix}` : ''}`;

  const fireHaptic = useCallback(
    (oldV: number, newV: number) => {
      if (!hapticBoundaries || oldV === newV) return;
      haptics.confirm();
      const lo = Math.min(oldV, newV);
      const hi = Math.max(oldV, newV);
      const crossed = hapticBoundaries.some((b) => b > lo && b <= hi);
      if (crossed) haptics.warn();
    },
    [hapticBoundaries],
  );

  const apply = useCallback(
    (delta: number) => {
      const next = clamp(round(value + delta, fractionDigits), min, max);
      if (next === value) return;
      fireHaptic(value, next);
      onChange(next);
    },
    [value, fractionDigits, min, max, onChange, fireHaptic],
  );

  const dec = () => apply(-step);
  const inc = () => apply(step);
  const jump = (delta: number) => apply(delta);

  const holdMultiplier = holdJump?.multiplier ?? 10;
  const holdDuration = holdJump?.durationMs ?? 600;
  const holdEnabled = !!holdJump;

  const decHold = useTapOrHold({
    enabled: holdEnabled,
    durationMs: holdDuration,
    onTap: dec,
    onHold: () => jump(-step * holdMultiplier),
  });
  const incHold = useTapOrHold({
    enabled: holdEnabled,
    durationMs: holdDuration,
    onTap: inc,
    onHold: () => jump(step * holdMultiplier),
  });

  // Render quick-jump buttons from largest to smallest on the left,
  // smallest to largest on the right — so the layout mirrors around
  // the center value. e.g. quickSteps=[10, 100] produces:
  //   [ -100 ] [ -10 ] [ - ]  value  [ + ] [ +10 ] [ +100 ]
  const leftJumps = quickSteps ? [...quickSteps].sort((a, b) => b - a) : [];
  const rightJumps = quickSteps ? [...quickSteps].sort((a, b) => a - b) : [];

  const decAriaLabel = holdEnabled
    ? `decrease by ${step}, hold to jump by ${step * holdMultiplier}`
    : 'decrease';
  const incAriaLabel = holdEnabled
    ? `increase by ${step}, hold to jump by ${step * holdMultiplier}`
    : 'increase';

  return (
    <div className="stepper" role="group" aria-label={ariaLabel}>
      {leftJumps.map((n) => (
        <button
          key={`dec-${n}`}
          type="button"
          className="stepper-btn stepper-btn-jump"
          onClick={() => jump(-n)}
          aria-label={`decrease by ${n}`}
          disabled={value <= min}
        >
          −{n}
        </button>
      ))}
      <button
        type="button"
        className="stepper-btn"
        aria-label={decAriaLabel}
        disabled={value <= min}
        {...(holdEnabled ? decHold.bind : { onClick: dec })}
      >
        <Icon icon={Minus} />
      </button>
      <span className={cn('stepper-value', large && 'stepper-value-large')} aria-live="polite">
        {display}
      </span>
      <button
        type="button"
        className="stepper-btn"
        aria-label={incAriaLabel}
        disabled={value >= max}
        {...(holdEnabled ? incHold.bind : { onClick: inc })}
      >
        <Icon icon={Plus} />
      </button>
      {rightJumps.map((n) => (
        <button
          key={`inc-${n}`}
          type="button"
          className="stepper-btn stepper-btn-jump"
          onClick={() => jump(n)}
          aria-label={`increase by ${n}`}
          disabled={value >= max}
        >
          +{n}
        </button>
      ))}
    </div>
  );
}

// Local tap-or-hold helper. A hold completes if the pointer stays within
// 8px of the press origin for `durationMs` — onHold fires once on the
// hold timer. A short release counts as a tap → onTap. Movement >8px
// or pointer cancel aborts both.
//
// Keyboard accessibility: the bind also exposes onClick. For pointer
// interactions we set handledByPointerRef in pointerup/timer-fire so
// the synthetic click that follows pointerup is a no-op. For keyboard
// activation (Enter/Space on the focused button), no pointer events
// fire — handledByPointerRef stays false and onClick fires onTap. That
// keeps Enter working even though we own the click handler. (Hold-to-
// jump remains touch-only; keyboard users still have the visible ±5
// quickStep + repeated Enter on ±1 to reach any value.)
//
// Lives here rather than in lib/ because it's stepper-specific;
// useHoldToConfirm in lib/holdToConfirm.ts is for confirmation
// surfaces (no on-tap behavior).
const HOLD_MOVE_TOLERANCE_PX = 8;

interface TapOrHoldOptions {
  enabled: boolean;
  durationMs: number;
  onTap: () => void;
  onHold: () => void;
}

function useTapOrHold({ enabled, durationMs, onTap, onHold }: TapOrHoldOptions) {
  const timerRef = useRef<number | null>(null);
  const heldRef = useRef(false);
  const movedRef = useRef(false);
  const startXYRef = useRef<{ x: number; y: number } | null>(null);
  const handledByPointerRef = useRef(false);

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const cancelTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      heldRef.current = false;
      movedRef.current = false;
      handledByPointerRef.current = false;
      startXYRef.current = { x: e.clientX, y: e.clientY };
      try {
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      } catch {
        // setPointerCapture can throw on certain elements; non-blocking.
      }
      timerRef.current = window.setTimeout(() => {
        heldRef.current = true;
        handledByPointerRef.current = true;
        timerRef.current = null;
        onHold();
      }, durationMs);
    },
    [enabled, durationMs, onHold],
  );

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!startXYRef.current) return;
    const dx = e.clientX - startXYRef.current.x;
    const dy = e.clientY - startXYRef.current.y;
    if (Math.hypot(dx, dy) > HOLD_MOVE_TOLERANCE_PX) {
      movedRef.current = true;
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, []);

  const onPointerUp = useCallback(() => {
    cancelTimer();
    const wasTap = !heldRef.current && !movedRef.current && startXYRef.current != null;
    startXYRef.current = null;
    if (wasTap) {
      handledByPointerRef.current = true;
      onTap();
    }
  }, [cancelTimer, onTap]);

  const onPointerCancel = useCallback(() => {
    cancelTimer();
    startXYRef.current = null;
  }, [cancelTimer]);

  // Synthetic click fires after pointerup (pointer paths) OR fires on
  // its own from keyboard Enter/Space (keyboard paths). For pointer:
  // bail — we already fired onTap/onHold. For keyboard: fire onTap.
  const onClick = useCallback(() => {
    if (handledByPointerRef.current) {
      handledByPointerRef.current = false;
      return;
    }
    onTap();
  }, [onTap]);

  return {
    bind: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onPointerLeave: onPointerCancel,
      onClick,
    },
  };
}

function clamp(x: number, lo: number, hi: number) { return Math.min(Math.max(x, lo), hi); }
function round(x: number, n: number) { const f = 10 ** n; return Math.round(x * f) / f; }
